import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { convertFile } from 'src/utils/convert-file.utils';
import { useSnackbar } from 'notistack';
import { usePartnersStore as useFileStore } from 'src/store/partners/partners.store';
import { ITerms } from 'src/types/app-terms';
import { useBoolean } from 'src/hooks/use-boolean';
import { PromoterTermsRepository } from 'src/repositories/personalization/promoter-terms/promoter-terms.repository';
import { makeApi } from 'src/http/api/internal/internal-http';

export const hasTextContent = (contentHtml: string) => {
  const textOnly = contentHtml.replace(/<[^>]*>/g, '').trim();
  return textOnly.length > 0;
};

export const usePromoterTerms = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const uploadGCPFile = useFileStore((store) => store.getUrlFile);
  const isLoading = useBoolean();
  const [termsToDisplay, setTermsToDisplay] = useState<ITerms>();

  const repository = new PromoterTermsRepository(makeApi());

  const findTerms = async () => {
    isLoading.onTrue();
    try {
      const response = await repository.findPromoterTerms();
      if (response.isLeft()) {
        throw new Error('Error fetching promoter terms');
      } else {
        setTermsToDisplay(response.value);
      }
    } catch {
      enqueueSnackbar('Ocorreu um erro ao buscar os termos do promotor', { variant: 'error' });
    } finally {
      isLoading.onFalse();
    }
  }

  const sendTerms = async (terms: ITerms) => {
    isLoading.onTrue();
    try {
      const response = await repository.sendPromoterTerms(terms);
      if (response.isLeft()) {
        throw new Error('Error sending promoter terms');
      }
      return true;
    } catch {
      enqueueSnackbar('Ocorreu um erro ao enviar os termos do promotor', { variant: 'error' });
      return false;
    } finally {
      isLoading.onFalse();
    }
  };

  const defaultValues = useMemo(
    () => ({
      title: termsToDisplay?.title || '',
      version: termsToDisplay?.version || '',
      contentHtml: termsToDisplay?.contentHtml || '',
      content: termsToDisplay?.contentUrl ? [termsToDisplay.contentUrl] : [],
    }),
    [termsToDisplay]
  );

  const Schema = Yup.object().shape({
    title: Yup.string().required('Título é obrigatório'),
    version: Yup.string(),
    contentHtml: Yup.string()
      .required('Conteúdo é obrigatório')
      .test('has-text-content', 'Conteúdo é obrigatório', hasTextContent),
    content: Yup.mixed().test(
      'has-at-least-one-file',
      'Arquivo é obrigatório',
      (value) => value && Array.isArray(value) && !!value.length
    ),
  });

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(Schema),
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
    reset: fillForm,
  } = methods;

  const changeVersion = (version: string) => {
    const versionParts = version?.split('.').map(Number);
    versionParts[2] += 1;
    if (versionParts[2] >= 10) {
      versionParts[2] = 0;
      versionParts[1] += 1;
      if (versionParts[1] >= 10) {
        versionParts[1] = 0;
        versionParts[0] += 1;
      }
    }
    return versionParts.join('.');
  };

  const onSubmit = handleSubmit(async (data) => {
    try {

      if (Array.isArray(data.content) && !!data.content.length) {
        data.content = data.content[0];
      }
      const [file, format] = await convertFile(data.content as File | string);
      if (format === 'error') {
        enqueueSnackbar('Ocorreu um erro ao criar o arquivo', { variant: 'error' });
        return;
      }
      if (format === 'url') {
        data.content = file;
      } else if (format === 'base64') {
        const uploaded = await uploadGCPFile(file);
        if (uploaded === 'error') {
          enqueueSnackbar('Ocorreu um erro ao criar o arquivo', { variant: 'error' });
          return;
        }
        data.content = uploaded;
      }

      const body = {
        ...data,
        contentUrl: data.content,
        version: changeVersion(data.version! || termsToDisplay?.version!) || termsToDisplay?.version!,
      };

      const success = await sendTerms(body as ITerms);

      if (success) {
        enqueueSnackbar('Termo criado com sucesso', { variant: 'success' });
        navigate('/admin/initial');
      } else {
        enqueueSnackbar('Ocorreu um erro ao criar o termo', { variant: 'error' });
      }
    } catch (error) {
      console.log(error);
    }
  });

  useEffect(() => {
    findTerms();
  }, []);

  useEffect(() => {
    if (termsToDisplay) {
      fillForm(termsToDisplay);
      setValue('content', [termsToDisplay.contentUrl]);
    }
  }, [termsToDisplay]);

  const handleDropMultiple = useCallback(
    (acceptedFiles: any[]) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const firstFile = acceptedFiles[0];
        if (firstFile) {
          if (!firstFile.type.includes('pdf')) {
            enqueueSnackbar('Por favor, envie apenas arquivos PDF.', { variant: 'error' });
            return;
          }
          const f = Object.assign(firstFile, {
            preview: URL.createObjectURL(firstFile),
          });

          setValue('content', [f], {
            shouldValidate: true,
          });
        }
      }
    },
    [setValue]
  );

  const handleRemove = (fileToRemove: string) => {
    handleRemoveAll();
  };

  const handleRemoveAll = () => {
    setValue('content', []);
  };

  return {
    loading: isLoading.value,
    isSubmitting,
    termsToDisplay,
    navigate,
    handleRemove,
    handleRemoveAll,
    handleDropMultiple,
    onSubmit,
    methods,
  };
};
